import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { APP_THEME } from "../theme/theme";
import { apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';

function VerifyOtp() {
  const navigate = useNavigate();
  const location = useLocation();
  const mobile = location.state?.mobile || "";
  const prevPage = location.state?.prevPage || "";
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false); // Added this state
  const inputRefs = useRef([]);
  const [otpToken, setOTPToken] = useState(location.state?.token || '')
  const [locationLoading, setLocationLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);

  const maskedMobile = mobile
    ? mobile.slice(0, 2) + "****" + mobile.slice(6)
    : "XXXXXXXXXX";

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    setLocationLoading(true);
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "GET_LOCATION_INFO"
        })
      );
    } else {
      toast.error("Location service not available");
      setLocationLoading(false);
    }
  }, [])

  window.addEventListener("message", async (event) => {
    try {
      // Check if event.data is a string or object
      let data;
      if (typeof event.data === 'string') {
        data = JSON.parse(event.data);
      } else {
        // If it's already an object, use it directly
        data = event.data;
      }

      if (data.type === "LOCATION_INFO") {
        setUserLocation(data);
        setLocationLoading(false);
      }
    } catch (e) {
      setLocationLoading(false);
      toast.error("Error in message listener:", e.message);
    }
  });

  const handleVerify = useCallback(async () => {
    const code = otp.join("");
    if (code.length !== 6) {
      toast.error("Please enter complete 6-digit OTP");
      return;
    }

    setVerifying(true);

    try {
      const result = await apiCall(
        "verify-otp",
        {
          number: mobile,
          otp: code,
          otp_token: otpToken,
          prevPage,
          longitude: userLocation?.longitude || null,
          latitude: userLocation?.latitude || null,
          address: userLocation?.address || null
        },
        false
      );

      if (result.status === true) {
        //toast.success("OTP verified successfully!");
        localStorage.setItem("credentials", JSON.stringify(result.data));
        navigate("/home", { replace: true });
      } else {
        toast.error(result.message || "Invalid OTP. Please try again.");
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  }, [otp, mobile, otpToken, navigate]);

  useEffect(() => {
    const code = otp.join("");
    if (code.length === 6 && !verifying) {
      const timeout = setTimeout(() => {
        handleVerify();
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [otp, handleVerify, verifying]);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    if (verifying) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    if (verifying) return;
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pastedData[i] || "";
      }
      setOtp(newOtp);
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleResend = async () => {
    if (!canResend || verifying || resending) return;

    setResending(true);

    try {
      const result = await apiCall(
        "login",
        { number: mobile },
        false
      );

      if (result.status === true) {
        setOTPToken(result.data.otp_token);
        toast.success("OTP resent successfully!");
        setTimer(30);
        setCanResend(false);
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        toast.error(result.message || "Failed to resend OTP. Please try again.");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col mx-auto overflow-y-auto"
      style={{
        background: APP_THEME.bgColor,
        padding: "24px",
        maxWidth: "480px",
      }}
    >
      {/* Back Button */}
      <button
        onClick={() => !verifying && navigate(-1)}
        disabled={verifying}
        className="bg-transparent w-10 h-10 rounded-full flex items-center justify-center mb-6 transition-transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          border: `1.5px solid ${APP_THEME.borderColor}`,
          color: APP_THEME.textPrimary,
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      </button>

      <h1
        className="text-2xl font-bold mb-2 text-left"
        style={{
          color: APP_THEME.textPrimary,
          fontFamily: APP_THEME.fontFamily,
        }}
      >
        Verify OTP
      </h1>

      <p
        className="text-sm mb-8 text-left"
        style={{
          color: APP_THEME.textSecondary,
          fontFamily: APP_THEME.fontFamily,
        }}
      >
        OTP sent to +91 {maskedMobile}
      </p>

      <div className="w-full max-w-[420px] self-center">
        <div
          className="rounded-2xl p-8 pb-6 mb-6 flex flex-col items-center"
          style={{
            background: APP_THEME.surfaceColor,
            border: `1px solid ${APP_THEME.borderColor}`,
            boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
          }}
        >
          <div
            className="flex gap-2 mb-6 justify-center w-full"
            onPaste={handlePaste}
          >
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="tel"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={verifying}
                className="w-[42px] h-12 text-center text-xl font-semibold rounded-xl outline-none transition-all duration-200 disabled:opacity-60"
                style={{
                  fontFamily: APP_THEME.fontFamily,
                  border: `1.5px solid ${digit ? APP_THEME.primaryColor : APP_THEME.borderColor}`,
                  color: APP_THEME.textPrimary,
                  background: APP_THEME.bgColor,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = APP_THEME.primaryColor;
                  e.target.style.boxShadow = `0 0 0 3px ${APP_THEME.primaryLight}40`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = digit ? APP_THEME.primaryColor : APP_THEME.borderColor;
                  e.target.style.boxShadow = "none";
                }}
              />
            ))}
          </div>

          <div className="w-full text-[13px] pl-1" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
            {canResend ? (
              <span
                onClick={handleResend}
                className={`font-semibold ${(verifying || resending) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                style={{ color: APP_THEME.primaryColor }}
              >
                {resending ? "Sending..." : "Resend OTP"}
              </span>
            ) : (
              <span>
                Resend OTP in <span style={{ color: APP_THEME.primaryColor, fontWeight: 600 }}>{timer}s</span>
              </span>
            )}
          </div>
        </div>

        <button
          onClick={handleVerify}
          disabled={verifying || otp.join("").length !== 6}
          className="w-full h-12 border-none rounded-lg text-[15px] font-semibold transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed"
          style={{
            background: verifying ? APP_THEME.primaryDark : APP_THEME.primaryColor,
            color: APP_THEME.textOnPrimary,
            fontFamily: APP_THEME.fontFamily,
          }}
          onMouseOver={(e) => {
            if (!verifying) e.currentTarget.style.background = APP_THEME.primaryDark;
          }}
          onMouseOut={(e) => {
            if (!verifying) e.currentTarget.style.background = APP_THEME.primaryColor;
          }}
        >
          {verifying ? (
            <div className="flex items-center justify-center gap-2">
              <div
                className="w-5 h-5 rounded-full animate-spin"
                style={{
                  border: `2px solid ${APP_THEME.textOnPrimary}`,
                  borderTop: `2px solid transparent`,
                }}
              />
              Verifying...
            </div>
          ) : (
            "Verify OTP"
          )}
        </button>
      </div>

      <div className="flex-1" />

      <div
        className="w-full pt-10 text-xs text-center leading-relaxed"
        style={{
          color: APP_THEME.textSecondary,
          fontFamily: APP_THEME.fontFamily,
        }}
      >
        By continuing, you agree to our{" "}
        <span
          onClick={() => !verifying && navigate("/terms")}
          className="cursor-pointer transition-opacity"
          style={{
            color: APP_THEME.primaryColor,
            opacity: verifying ? 0.5 : 1,
            cursor: verifying ? "not-allowed" : "pointer",
          }}
        >
          Terms
        </span>{" "}
        &{" "}
        <span
          onClick={() => !verifying && navigate("/privacy")}
          className="cursor-pointer transition-opacity"
          style={{
            color: APP_THEME.primaryColor,
            opacity: verifying ? 0.5 : 1,
            cursor: verifying ? "not-allowed" : "pointer",
          }}
        >
          Privacy
        </span>
      </div>

      {/* Location Loading Overlay */}
      {locationLoading && (
        <>
          {/*
        <div
            className="fixed inset-0 backdrop-blur-md bg-black/40 z-[1200] animate-fadeIn"
            style={{ backdropFilter: 'blur(8px)' }}
          />
          <div className="fixed inset-0 z-[1201] flex items-center justify-center">
            <div className="bg-white rounded-2xl p-8 flex flex-col items-center shadow-2xl animate-scaleIn max-w-[320px] w-full mx-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-gray-200 border-t-4 animate-spin"
                  style={{ borderTopColor: APP_THEME.primaryColor }} />
              </div>
              <div className="mt-4 text-center">
                <h3 className="text-lg font-semibold" style={{ color: APP_THEME.textPrimary }}>
                  Fetching Location
                </h3>
                <p className="text-sm mt-1" style={{ color: APP_THEME.textSecondary }}>
                  Please allow location access
                </p>
              </div>
            </div>
          </div>
        */}
        </>
      )}

      <style>
        {`
          .active\\:scale-95:active {
            transform: scale(0.95);
          }
        `}
      </style>
      <Toaster position="bottom-center" />
    </div>
  );
}

export default VerifyOtp;